import React from 'react';
import { Box, IconButton, Modal, Typography } from '@mui/material';
import { observer } from 'mobx-react';
import BaseInput from '../../../components/BaseInput';
import BaseButton from '../../../components/BaseButton';
import adminStore from '../../../store/Admin';
import dataStore from '../../../store';

const CreditModal = observer(
  ({
    open,
    handleClose,
    data,
  }: {
    open: boolean;
    handleClose: any;
    data?: any;
  }) => {
    const [credits, setCredits] = React.useState('');
    const [errors, setErrors] = React.useState({});
    const currentBalance = Number(data?.balance ?? 0);
    const numericCredits = Number(credits || '0');
    const resultBalance =
      data?.action === 'grant'
        ? currentBalance + numericCredits
        : Math.max(0, currentBalance - numericCredits);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const rawValue = e.target.value;

      if (rawValue === '') {
        setCredits('');
        return;
      }

      if (/^[0-9]+$/.test(rawValue)) {
        const normalized = String(Number(rawValue)); // removes leading zeros
        setCredits(normalized);
      }
    };

    const storeData = async () => {
      setErrors({});

      if (numericCredits <= 0 || isNaN(numericCredits)) {
        setErrors({ credits: 'Credits must be greater than 0' });
        return;
      }

      const result = await adminStore.changeCustomerCredit(
        data?.customer,
        numericCredits,
        data?.action,
        data?.type
      );

      if (result) {
        dataStore.getUserInternalInfo();
        handleClose(true);
      }
    };

    return (
      <Modal
        open={open}
        aria-labelledby='search-pipeline-modal-title'
        aria-describedby='search-pipeline-modal-description'
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
        }}
      >
        <Box
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            width: { xs: '80%', sm: '80%', md: '40%', lg: '40%', xl: '40%' },
            maxWidth: 'xl',
            maxHeight: '90vh',
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderBottom: '1px solid #ddd',
              backgroundColor: '#fff',
              px: 3,
              py: 2,
            }}
          >
            <Typography id='modal-title' variant='h6'>
              {data?.action === 'grant' ? 'Grant Credits' : 'Remove Credits'}
            </Typography>
            <IconButton
              onClick={() => {
                handleClose();
              }}
            >
              x
            </IconButton>
          </Box>

          {/* Content */}
          <Box sx={{ p: 3, pb: 0 }}>
            <BaseInput
              label={'Credits'}
              value={credits}
              name={'credits'}
              onChange={handleInputChange}
              errors={errors}
              type='number'
              required={true}
            />

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mt: 4,
                mb: 2,
              }}
            >
              {/* Credit change */}
              <Box
                sx={{
                  flex: 1,
                  border: '1px solid #ccc',
                  backgroundColor: '#fafafa',
                  borderRadius: 2,
                  px: 2,
                  py: 1,
                  mr: 2,
                  textAlign: 'center',
                }}
              >
                <Typography variant='subtitle2' color='text.secondary'>
                  {data?.action === 'grant' ? 'Granting' : 'Removing'}
                </Typography>
                <Typography
                  variant='h4'
                  sx={{
                    fontWeight: 'bold',
                    color:
                      data?.action === 'grant' ? 'success.main' : 'error.main',
                  }}
                >
                  {data?.action === 'grant'
                    ? `+${numericCredits}`
                    : `-${numericCredits}`}
                </Typography>
              </Box>

              {/* New state */}
              <Box
                sx={{
                  flex: 1,
                  border: '1px solid #ccc',
                  borderRadius: 2,
                  px: 2,
                  py: 1,
                  textAlign: 'center',
                  backgroundColor: '#f9f9f9',
                }}
              >
                <Typography variant='subtitle2' color='text.secondary'>
                  New Balance
                </Typography>
                <Typography variant='h6' fontWeight='medium'>
                  {resultBalance}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              gap: 2,
              p: 3,
              borderTop: '1px solid #ddd',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <BaseButton
              text='Close'
              variant='outlined'
              color='primary'
              isDisabled={adminStore.isProcessing}
              spinner={adminStore.isProcessing}
              onClick={() => {
                handleClose();
              }}
            />
            <BaseButton
              text='Save'
              variant='contained'
              color='success'
              isDisabled={adminStore.isProcessing}
              spinner={adminStore.isProcessing}
              onClick={storeData}
            />
          </Box>
        </Box>
      </Modal>
    );
  }
);

export default CreditModal;
